export type SecurityPlanTier = "completeIndividual" | "completeFamily";

export const getSecurityPlanId = (plan: any): string | undefined => {
  const raw = plan?._id ?? plan?.id ?? plan?.planId ?? "";
  const normalized = String(raw || "").trim();
  return normalized || undefined;
};

export const normalizeSecurityPlanTierFromText = (
  value: unknown,
): SecurityPlanTier | null => {
  const text = String(value || "").toLowerCase();
  if (text.includes("family") && text.includes("complete")) {
    return "completeFamily";
  }
  if (text.includes("individual") && text.includes("complete")) {
    return "completeIndividual";
  }
  return null;
};

export const resolveIncludedSecurityPlanTierFromSimData = (
  simData: any,
): SecurityPlanTier | null => {
  if (!simData || typeof simData !== "object") return null;
  const candidates = [
    (simData as any)?.securityPlanIncluded?.securityPlanName,
    (simData as any)?.securityPlanIncluded?.planName,
    (simData as any)?.securityPlanIncluded?.name,
  ];
  for (const value of candidates) {
    const tier = normalizeSecurityPlanTierFromText(value);
    if (tier) return tier;
  }
  return null;
};

export const resolveIncludedSecurityPlanIdFromSimData = (
  simData: any,
): string | undefined => {
  if (!simData || typeof simData !== "object") return undefined;
  const idCandidates = [
    (simData as any)?.securityPlanIncluded?._id,
    (simData as any)?.securityPlanIncluded?.id,
    (simData as any)?.securityPlanIncluded?.planId,
  ];
  for (const value of idCandidates) {
    const normalized = String(value || "").trim();
    if (normalized) return normalized;
  }
  return undefined;
};

/**
 * Tier of the **selected** checkout security plan for PSD (not “included vs upsell” branching only).
 * When `securityPlanId` matches the SIM included plan (or is unset), uses included plan names.
 * Otherwise looks up `securityPlanId` in common `simData` security plan arrays when present.
 */
export const resolvePsdSelectedSecurityPlanTierForSkeleton = (
  simData: unknown,
  selectedSecurityPlanId: string | undefined,
): SecurityPlanTier | null => {
  const selected = String(selectedSecurityPlanId || "").trim();
  const includedId = String(resolveIncludedSecurityPlanIdFromSimData(simData) || "").trim();

  if (!selected || !includedId || selected === includedId) {
    return resolveIncludedSecurityPlanTierFromSimData(simData);
  }

  const data = simData && typeof simData === "object" ? (simData as Record<string, unknown>) : null;
  if (!data) return null;

  const tierFromList = (list: unknown): SecurityPlanTier | null => {
    if (!Array.isArray(list)) return null;
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const p = item as Record<string, unknown>;
      const id = String(p._id ?? p.id ?? p.planId ?? "").trim();
      if (id !== selected) continue;
      return (
        normalizeSecurityPlanTierFromText(
          p.securityPlanName ?? p.planName ?? p.name,
        ) || null
      );
    }
    return null;
  };

  return (
    tierFromList(data.securityPlans) ||
    tierFromList(data.inventorySecurityPlans) ||
    tierFromList(data.mobileSimSecurityPlans) ||
    null
  );
};
